import React from 'react';
import isEqual from 'react-fast-compare';

import {DomainGraphInterface} from 'app-core/framework/CoreObjectConstants';
import {asCoreObject, isCoreObjectWithInterface} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {handleToggleStar} from 'app-core/platform/starrable/CoreObjectStarIcon';
import isArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {getFeatureStatus} from 'auth/feature-enablement/FeatureStatus';
import {hasReactionContext} from 'chemistry/ChemSearchUtils';
import DataConnector from 'connect/DataConnector';
import {FolderSummary} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import * as SourceConstants from 'constants/SourceConstants';
import {User} from 'constants/UserConstants';
import FileBrowserUtil, {ResultWithDate} from 'file-browser/FileBrowserUtil';
// @ts-expect-error
import FileMenu from 'file-browser/FileMenu';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import * as NavUtil from 'nav/NavUtil';
import {Party} from 'parties/PartyConstants';
import * as RegistryIdentifierUtilRest from 'registry/RegistryIdentifierUtilRest';
import BioentityValidationStatus from 'registry/validation/BioentityValidationStatus';
import {ListableItem} from 'search/SearchConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ShortcutIcon from 'ui-library/icons/ShortcutIcon';
import APIUtil from 'util/APIUtil';
import * as ShortcutUtil from 'util/ShortcutUtil';
import {ElementMouseEvent, Optional} from 'util/Types';

import DragAndDropSidepanelItemWrapper from './DragAndDropSidepanelItemWrapper';
import SidepanelItem from './SidepanelItem';

interface HasHumanId {
  humanId?: string;
}

interface HasFolder {
  folder?: FolderSummary;
}

function hasHumanId(item: ListableItem): item is ListableItem & HasHumanId {
  return item.hasOwnProperty('humanId');
}

function hasFolder(item: ListableItem): item is ListableItem & HasFolder {
  return item.hasOwnProperty('folder');
}

function isBioentity(item: ListableItem): item is ListableItem & BioentitySummary {
  return APIUtil.isBiologicalEntity(item.id);
}

function isResultWithDate(item: ListableItem): item is ListableItem & ResultWithDate {
  return item.hasOwnProperty('modified_at') || item.hasOwnProperty('modifiedAt');
}

export interface FolderFileSidepanelItemProps {
  active: boolean;
  authors?: Array<Party>;
  collection: Optional<string>;
  bioentityAliasInfos?: Array<BioentityAliasInfo>;
  finderId?: string | null;
  isListing: boolean;
  isCompact: boolean;
  openResult(result: ListableItem): void;
  result: ListableItem;
  isChild: boolean;
  isStarred?: boolean;
}

interface State {
  isMenuOpen: boolean;
}

interface EnhancedProps {
  authors: Array<User>;
}

class FolderFileSidepanelItemConnector extends DataConnector<FolderFileSidepanelItemProps, EnhancedProps> {
  enhanceProps(): EnhancedProps {
    return {
      authors: APIUtil.hasAuthors(this.props.result.id)
        ? this.getOrFetchOptional(ManagedModelType.AUTHORS_BY_ITEM_ID, this.props.result.id) || []
        : [],
    };
  }
}

// FolderFileSidepanelItem renders items that are "hosted" on our filesystem - folders and folder items.
export class FolderFileSidepanelItem extends React.PureComponent<
  FolderFileSidepanelItemProps & EnhancedProps,
  State
> {
  private _isMounted = false;

  override state: State = {
    isMenuOpen: false,
  };

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  private handleOpen = (): void => {
    this.props.openResult(this.props.result);
  };

  private handleContextMenuClose = (): void => {
    if (this._isMounted) {
      this.setState({isMenuOpen: false});
    }
  };

  private handleShowMenuOptions = (e: ElementMouseEvent): void => {
    assert(this.props.finderId != null, 'Expected a non-null finderId');
    FileMenu.open(e, {
      file: this.props.result,
      finderId: this.props.finderId,
      isListing: this.props.isListing,
      closeCallback: this.handleContextMenuClose,
    });
    this.setState({isMenuOpen: true});
  };

  renderTitle(): React.ReactNode {
    const {bioentityAliasInfos, result} = this.props;
    const {name} = result;
    let titleNode = [];
    if (hasHumanId(result)) {
      // If this assertion ever starts failing (which will happen if we decide to support
      // e.g. bioentity alias infos for entries), then we'll need to refactor this method to support
      // both humanIds and bioentityAliasInfos. Leaving this as tech debt until we need that.
      assert(
        !bioentityAliasInfos || bioentityAliasInfos.length === 0,
        'Did not expect to have both humanId and bioentityAliasInfos'
      );
      titleNode = [
        <span key="name" className="sidepanel-primaryLabel">
          {result.name}{' '}
        </span>,
        <span key="humanId" className="sidepanel-secondaryLabel">
          {result.humanId}
        </span>,
      ];
    } else if (bioentityAliasInfos && bioentityAliasInfos.length > 0) {
      titleNode = [
        <span key="alias">
          {RegistryIdentifierUtilRest.getAliasComponents(bioentityAliasInfos, name, this.props.collection)}
        </span>,
      ];
    } else {
      titleNode = [<span key="name">{name}</span>];
    }

    if (isBioentity(result)) {
      titleNode.push(<BioentityValidationStatus key="validationStatus" bioentity={result} />);
    }

    return titleNode;
  }

  renderSubTitle(): string | null {
    const isCompact = NavUtil.isCompactDisplay();
    const resultDate = isResultWithDate(this.props.result)
      ? FileBrowserUtil.getResultDate(this.props.result, {
          fuzzy: true,
          compact: isCompact,
        })
      : undefined;
    if (resultDate?.date == null || resultDate?.date?.length === 0) {
      return null;
    }
    return `${isCompact ? '' : resultDate.prefix}${resultDate.date}`;
  }

  renderIcon(result: ListableItem): JSX.Element {
    if (ShortcutUtil.shouldShowShortcut(result, this.props.finderId ?? undefined)) {
      return <ShortcutIcon icon={FileBrowserUtil.getFileIconName(result)} />;
    }
    // TODO(BNCH-145194): remove this custom icon when removing reaction search prototype
    if (hasReactionContext(result)) {
      return <BenchlingIcon icon="reaction-vessel" />;
    }
    return <BenchlingIcon icon={FileBrowserUtil.getFileIconName(result)} />;
  }

  override render(): JSX.Element {
    assert(this.props.finderId != null, 'Expected a non-null finderId');
    const modelType = APIUtil.apiIdToType(this.props.result.id);
    const isFolder = modelType === ModelTypeConstants.FOLDER;
    const isRegistered =
      hasFolder(this.props.result) &&
      this.props.result.folder?.source?.sourceType === SourceConstants.REGISTRY_TYPE;
    const featureStatus = getFeatureStatus({
      feature: StandardFeature.OPEN,
      item: this.props.result,
      itemState: {isRegistered},
    });
    return (
      <DragAndDropSidepanelItemWrapper
        canInteract={this.state.isMenuOpen}
        finderId={this.props.finderId}
        result={this.props.result}
        shouldCaptureDrop={isFolder}
      >
        <SidepanelItem
          active={this.props.active}
          className="sidepanelItem--dnd"
          showStar={isCoreObjectWithInterface(this.props.result, DomainGraphInterface.STARRABLE)}
          handleClickStar={() => {
            handleToggleStar({
              item: asCoreObject(this.props.result),
              isStarred: this.props.isStarred ?? false,
            });
          }}
          starred={this.props.isStarred}
          icon={this.renderIcon(this.props.result)}
          isCompact={this.props.isCompact}
          faded={!featureStatus.enabled || (isArchivable(this.props.result) && isArchived(this.props.result))}
          onClick={this.handleOpen}
          onContextMenu={this.handleShowMenuOptions}
          parties={this.props.authors}
          subTitle={this.renderSubTitle()}
          title={{text: this.props.result.name, node: this.renderTitle()}}
          tooltipText={!featureStatus.enabled ? featureStatus.userMessage : undefined}
          isExpandable={false}
          isChild={this.props.isChild}
        />
      </DragAndDropSidepanelItemWrapper>
    );
  }
}

export default FolderFileSidepanelItemConnector.connect(FolderFileSidepanelItem, {
  throttleUpdates: true,
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});
